import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Fade,
  useTheme,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { hardRedirectToLogin } from "../../utils/authRecovery";
import logoDark from "../../assets/Lockup Horizontal Dark.svg";
import logoLight from "../../assets/Lockup Horizontal Light.svg";

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

export function AuthCallback() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [timedOut, setTimedOut] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  // Safety timeout if SDK never resolves
  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), 15_000);
    return () => clearTimeout(timer);
  }, []);

  // Once user is available, show welcome briefly then navigate
  useEffect(() => {
    if (isLoading || !user) return;

    setShowWelcome(true);
    const timer = setTimeout(() => navigate("/", { replace: true }), 1500);
    return () => clearTimeout(timer);
  }, [user, isLoading, navigate]);

  if ((!isLoading && !user) || timedOut) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Authentication Issue
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 600, mb: 2 }}
        >
          {timedOut
            ? "Authentication is taking too long. Please try again."
            : "Authentication failed or session expired. Please try signing in again."}
        </Typography>
        <Button variant="contained" onClick={hardRedirectToLogin}>
          Return to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 3,
        background: theme.palette.background.default,
      }}
    >
      <Fade in={!showWelcome} timeout={300} unmountOnExit>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                border: `3px solid ${theme.palette.divider}`,
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
            />
            <CircularProgress
              size={48}
              sx={{ color: "primary.main", position: "absolute" }}
            />
          </Box>
          <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 500 }}>
            Signing you in...
          </Typography>
        </Box>
      </Fade>

      <Fade in={showWelcome} timeout={500}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, position: showWelcome ? "static" : "absolute" }}>
          <Box
            component="img"
            src={theme.palette.mode === "dark" ? logoLight : logoDark}
            alt="KARDA Logo"
            sx={{ width: 200, mb: 1 }}
          />
          <Typography variant="h4" sx={{ color: "text.primary", fontWeight: 700 }}>
            Welcome back{user?.firstName ? `, ${user.firstName}` : ""}!
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Taking you to your dashboard...
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
}
